import CryptoJS from 'crypto-js';
import { env } from '@core/config/env';
import type { AuthResponse } from '@modules/auth/services/api';

const TOKEN_KEY = 'auth-token';
const USER_DATA_KEY = 'user-data';
const SELECTED_GAME_KEY = 'selected-game-id';
const SECRET_KEY = env.encryptionKey;

// Generic encryption/decryption utilities
export const encryptData = (data: string): string => {
  return CryptoJS.AES.encrypt(data, SECRET_KEY).toString();
};

export const decryptData = (encryptedData: string): string | null => {
  try {
    if (!encryptedData) {
      return null;
    }
    const bytes = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);

    // For empty strings, CryptoJS may return empty string but with valid sigBytes
    // We need to check if the decryption was successful by checking if we got a valid result
    // If sigBytes is 0 and decrypted is empty, it means the original was empty
    // If sigBytes is 0 and we have invalid data, it would throw an error or return undefined
    if (bytes.sigBytes === 0 && decrypted === '') {
      return '';
    }

    // For non-empty data, check if decryption worked
    if (bytes.sigBytes <= 0) {
      return null;
    }

    return decrypted;
  } catch {
    return null;
  }
};

// Token utilities (backward compatibility)
export const encryptToken = (token: string): string => {
  return encryptData(token);
};

export const decryptToken = (encryptedToken: string): string | null => {
  return decryptData(encryptedToken);
};

// Get stored token from localStorage
export const getStoredToken = (): string | null => {
  const encryptedToken = localStorage.getItem(TOKEN_KEY);
  if (encryptedToken) {
    return decryptToken(encryptedToken);
  }
  return null;
};

// Store token in localStorage
export const storeToken = (token: string): void => {
  const encryptedToken = encryptToken(token);
  localStorage.setItem(TOKEN_KEY, encryptedToken);
};

// Remove token from localStorage
export const removeToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
};

// User data storage utilities
export interface StoredUserData extends Omit<AuthResponse, 'token'> {}

// Store complete user data in localStorage with encryption
export const storeUserData = (userData: StoredUserData): void => {
  try {
    const jsonData = JSON.stringify(userData);
    const encryptedData = encryptData(jsonData);
    localStorage.setItem(USER_DATA_KEY, encryptedData);
  } catch (error) {
    console.error('Failed to store user data:', error);
  }
};

// Get stored user data from localStorage with decryption
export const getStoredUserData = (): StoredUserData | null => {
  try {
    const encryptedData = localStorage.getItem(USER_DATA_KEY);
    if (!encryptedData) {
      return null;
    }

    const decryptedData = decryptData(encryptedData);
    if (!decryptedData) {
      return null;
    }

    return JSON.parse(decryptedData) as StoredUserData;
  } catch (error) {
    console.error('Failed to retrieve user data:', error);
    return null;
  }
};

// Remove user data from localStorage
export const removeUserData = (): void => {
  localStorage.removeItem(USER_DATA_KEY);
};

// Clear all auth data (token + user data + selected game)
export const clearAllAuthData = (): void => {
  removeToken();
  removeUserData();
  removeSelectedGameId();
};

// Check if user is authenticated (has valid token and user data)
export const isAuthenticated = (): boolean => {
  const token = getStoredToken();
  const userData = getStoredUserData();
  return !!(token && userData);
};

// Selected game ID utilities
export const setSelectedGameId = (gameId: number): void => {
  localStorage.setItem(SELECTED_GAME_KEY, gameId.toString());
};

export const getSelectedGameId = (): number | null => {
  const gameId = localStorage.getItem(SELECTED_GAME_KEY);
  return gameId ? parseInt(gameId, 10) : null;
};

export const removeSelectedGameId = (): void => {
  localStorage.removeItem(SELECTED_GAME_KEY);
};

export const getGameId = (): number => {
  const userData = getStoredUserData();
  if (!userData?.games?.length) throw new Error('No games found in user data');
  
  // Check if there's a selected game ID
  const selectedGameId = getSelectedGameId();
  if (selectedGameId) {
    // Verify the selected game ID exists in user's games
    const gameExists = userData.games.some(game => game.game_id === selectedGameId);
    if (gameExists) {
      return selectedGameId;
    }
  }
  
  // Default to first game if no selection or invalid selection
  const defaultGameId = userData.games[0].game_id;
  setSelectedGameId(defaultGameId);
  return defaultGameId;
};
